import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import Page from "components/page/Page";
import DataTable from "components/dataTable/DataTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faPlus } from "@fortawesome/pro-regular-svg-icons";
import Card from "components/card/Card";
import { Badge } from "components/badge/Badge";
import "./units.scss";
import { GetAccommodationUnits } from "api/rpc/2024-04/facilityAdmin/accommodation/units";
import { GetAccommodationUnitTypes } from "api/rpc/2024-04/facilityAdmin/accommodation/unitTypes";
import { GetAccommodationBuildings } from "api/rpc/2024-04/facilityAdmin/accommodation/building";
import { IAccommodationUnitType } from "../unitTypes/UnitTypes";
import { IAccommodationBuilding } from "../buildings/Buildings";
import { Select } from "components/select/index";

export interface IAccommodationUnit {
  id: number;
  building_id: number;
  unit_type_id: number;
  title: string;
  code: string;
  floor: string;
  status: "active" | "out_of_service";
}

export function Units() {
  const history = useHistory();
  const { t } = useTranslation();

  const [accommodationUnits, setAccommodationUnits] = useState<IAccommodationUnit[]>([]);

  const [accommodationUnitTypes, setAccommodationUnitTypes] = useState(undefined);
  const [accommodationBuildings, setAccommodationBuildings] = useState(undefined);

  const [filterState, setFilterState] = useState({
    search: "",
    offset: 0,
    limit: 10,
    unit_type_id: -1,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAccommodationUnits(source.token);

    return () => {
      source.cancel();
    };
  }, [filterState]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAccommodationBuildings();
    void loadAccommodationUnitTypes();
    return () => {
      source.cancel();
    };
  }, []);

  async function loadAccommodationUnits(token?: CancelToken) {
    if (accommodationUnits !== undefined) {
      setAccommodationUnits(undefined);
    }
    const accommodationUnitsRes = await GetAccommodationUnits(null, false);

    if (token && token.reason) {
      return;
    }
    if (accommodationUnitsRes.status !== StatusCode.OK) {
      setAccommodationUnits([]);
      return;
    }

    setAccommodationUnits(accommodationUnitsRes.data as IAccommodationUnit[]);
  }

  async function loadAccommodationUnitTypes(token?: CancelToken) {
    if (accommodationUnitTypes !== undefined) {
      setAccommodationUnitTypes(undefined);
    }
    const accommodationUnitTypesRes = await GetAccommodationUnitTypes(null, false);

    if (token && token.reason) {
      return;
    }
    if (accommodationUnitTypesRes.status !== StatusCode.OK) {
      setAccommodationUnitTypes([]);
      return;
    }

    setAccommodationUnitTypes(accommodationUnitTypesRes.data);
  }

  async function loadAccommodationBuildings(token?: CancelToken) {
    if (accommodationBuildings !== undefined) {
      setAccommodationBuildings(undefined);
    }
    const accommodationBuildingsRes = await GetAccommodationBuildings(null, false);

    if (token && token.reason) {
      return;
    }
    if (accommodationBuildingsRes.status !== StatusCode.OK) {
      setAccommodationBuildings([]);
      return;
    }

    setAccommodationBuildings(accommodationBuildingsRes.data);
  }

  return (
    <Page className="mt-4">
      <Card
        title="Units"
        subtitle="Add or manage individual rooms/sites tied to a unit type."
        titleActions={[
          {
            action: () => history.push("/admin/accommodation/units/new"),
            content: (
              <>
                <FontAwesomeIcon icon={faPlus} style={{ marginRight: "6px" }} />
                {t("New Unit")}
              </>
            ),
            buttonType: "primary",
          },
        ]}
      >
        <Card.Section>
          <div className="w-sm">
            <Select
              onChange={(value: number) => setFilterState(prev => ({ ...prev, unit_type_id: Number(value) }))}
              defaultValue={-1}
              placeholder="Select unit type"
              label="Filter unit type"
            >
              <Select.Option value={-1} name={"All"}>
                {t("All")}
              </Select.Option>
              {accommodationUnitTypes?.map((type: IAccommodationUnitType) => (
                <Select.Option key={type.id} value={type.id} name={type.title}>
                  {type.title}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Card.Section>

        <div>
          <DataTable
            noRoundedTop
            columns={[
              { label: t("Name"), width: "17%" },
              { label: t("Floor"), width: "17%" },
              { label: t("Code"), width: "17%" },
              { label: t("Unit type"), width: "22%" },
              { label: t("Building / Location"), width: "22%" },

              { label: "", width: "5%" },
            ]}
            loading={accommodationUnits === undefined}
            footer={{
              tableLimit: filterState?.limit,
              tableOffset: filterState?.offset,
              handleTableOffset: direction =>
                setFilterState(prev => ({
                  ...prev,
                  offset: direction === "prev" ? prev.offset - prev.limit : prev.offset + prev.limit,
                })),
              disableNextOffset: !accommodationUnits || !(accommodationUnits?.length === filterState?.limit),
            }}
            overflow
          >
            {accommodationUnits
              ?.filter((unit: IAccommodationUnit) => {
                return filterState.unit_type_id === -1 || unit.unit_type_id === filterState.unit_type_id;
              })
              ?.map((unit: IAccommodationUnit, index: number) => {
                const unitType = accommodationUnitTypes?.find(
                  (type: IAccommodationUnitType) => type.id === unit?.unit_type_id,
                );
                const building = accommodationBuildings?.find(
                  (b: IAccommodationBuilding) => b.id === unit?.building_id,
                );

                return (
                  <tr
                    key={index}
                    className="clickable"
                    onClick={() => history.push("/admin/accommodation/units/" + String(unit?.id))}
                  >
                    <td>{unit?.title}</td>

                    <td>{unit?.floor}</td>

                    <td>{unit?.code}</td>

                    <td>
                      <Badge type="gray" size="small">
                        {unitType?.title}
                      </Badge>
                    </td>

                    <td>{building?.title}</td>

                    <td className="text-right">
                      <FontAwesomeIcon
                        icon={faPencil}
                        onClick={() => history.push("/admin/accommodation/units/" + String(unit?.id))}
                        className="cursor-pointer"
                      />
                    </td>
                  </tr>
                );
              })}
          </DataTable>
        </div>
      </Card>
    </Page>
  );
}
